import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { locallyMarkPromotionalTipAsSeen, markPromotionalTipAsSeen } from '../../../db/app/actions';
import { PromotionalTip } from '../../../db/shared/types';
import { useRequestDispatch } from '../../../utils/request-actions';

export interface UsePromotionalTipPanel {
  isOpen: boolean;
  onClose: () => void;
  promotionalTip: PromotionalTip;
}

const usePromotionalTipPanel = ({ isOpen, onClose, promotionalTip }: UsePromotionalTipPanel) => {
  const dispatch = useDispatch();
  const requestDispatch = useRequestDispatch();

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const isLastStep = currentStepIndex === promotionalTip.panels.length - 1;

  const [isTipRead, setIsTipRead] = useState(false);

  const currentStep = useMemo(() => {
    return promotionalTip.panels[currentStepIndex];
  }, [currentStepIndex, promotionalTip.panels]);

  const canSeePrevious = promotionalTip.panels.length > 1;
  const canVisitPrevious = currentStepIndex > 0;

  const handlePrevious = () => {
    setCurrentStepIndex((prevStepIndex) => {
      if (prevStepIndex > 0) return prevStepIndex - 1;
      return prevStepIndex;
    });
  };

  const canVisitNext = currentStepIndex < promotionalTip.panels.length - 1;

  const handleNext = () => {
    setCurrentStepIndex((prevStepIndex) => {
      if (prevStepIndex < promotionalTip.panels.length - 1) return prevStepIndex + 1;
      return prevStepIndex;
    });
  };

  useEffect(
    function preloadImages() {
      promotionalTip.panels.forEach((panel) => {
        const img = new Image();
        img.src = panel.image;
      });
    },
    [promotionalTip.panels]
  );

  useEffect(
    function onOpen() {
      if (!isOpen) return;
      setCurrentStepIndex(0);
    },
    [isOpen]
  );

  useEffect(
    function onReadTip() {
      if (!isOpen) return;
      if (!isLastStep) return;
      if (isTipRead) return;
      requestDispatch(markPromotionalTipAsSeen, { id: promotionalTip.id });
      setIsTipRead(true);
    },
    [isOpen, isLastStep, isTipRead, promotionalTip.id, requestDispatch]
  );

  const handleClose = async () => {
    onClose();
    if (!isTipRead) return;
    // allow dialog to finish its transition before removing the element that renders the dialog
    await new Promise((resolve) => setTimeout(resolve, 300));
    dispatch(locallyMarkPromotionalTipAsSeen({ id: promotionalTip.id }));
  };

  return {
    currentStep,
    isLastStep,
    canSeePrevious,
    canVisitPrevious,
    handlePrevious,
    canVisitNext,
    handleNext,
    handleClose,
  };
};

export default usePromotionalTipPanel;
